import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Stack, Typography } from "@mui/material";
import { apiFetch } from "../../api";
import PostventaFilters from "../components/PostventaFilters";
import PostventaTable from "../components/PostventaTable";
import PostventaFichaDialog from "../components/PostventaFichaDialog";
import { colors } from "../components/postventaUtils";

export default function PostventaContainer() {
  const [rows, setRows] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [vendedorId, setVendedorId] = useState("TODOS");
  const [tipo, setTipo] = useState("TODOS");
  const [estado, setEstado] = useState("TODOS");
  const [updatingId, setUpdatingId] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 260);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(0);
  }, [vendedorId, tipo, estado]);

  const fetchVendedores = useCallback(async () => {
    try {
      const res = await apiFetch("/api/vendedores/listado?ocultar_inactivos=true&limit=1000", { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setVendedores(Array.isArray(data) ? data : data.items || []);
    } catch (e) {
      setVendedores([]);
    }
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({
        page: String(page + 1),
        limit: String(limit),
        tipo,
        estado_postventa: estado,
      });
      if (search) params.set("search", search);
      if (vendedorId !== "TODOS") params.set("vendedor_id", String(vendedorId));

      const res = await apiFetch(`/api/ventas/postventa?${params.toString()}`, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRows(Array.isArray(data.items) ? data.items : []);
      setTotal(data.total || 0);
    } catch (e) {
      setError("No se pudo cargar postventa.");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, vendedorId, tipo, estado]);

  useEffect(() => {
    fetchVendedores();
  }, [fetchVendedores]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const toggleEstado = async (row) => {
    const next = row.estado_postventa === "ENTREGADO" ? "EN_ESPERA" : "ENTREGADO";
    setUpdatingId(row.id);
    setRows((prev) => prev.map((item) => item.id === row.id ? { ...item, estado_postventa: next } : item));
    try {
      const res = await apiFetch(`/api/ventas/${row.id}/postventa-estado`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ estado_postventa: next }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchRows();
    } catch (e) {
      await fetchRows();
    } finally {
      setUpdatingId(null);
    }
  };

  const clearFilters = () => {
    setSearchInput("");
    setSearch("");
    setVendedorId("TODOS");
    setTipo("TODOS");
    setEstado("TODOS");
    setPage(0);
  };

  const subtitle = useMemo(() => {
    if (!total) return "Sin registros cargados";
    return `Mostrando ${rows.length} de ${total} registros`;
  }, [rows.length, total]);

  return (
    <Box sx={{ height: "100%", overflow: "auto", px: { xs: 2, md: 4 }, py: 3 }}>
      <Box sx={{ width: "100%" }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", md: "flex-start" }} spacing={2} sx={{ mb: 3 }}>
          <Box>
            <Typography sx={{ color: colors.ink, fontSize: 28, fontWeight: 900, lineHeight: 1.1 }}>
              Clientes
            </Typography>
            <Typography sx={{ mt: 0.8, color: colors.muted, fontSize: 13, maxWidth: 520 }}>
              Hacé seguimiento de las entregas y el estado de postventa de tus clientes.
              Desde acá podés ver el tipo de pago, la ficha del cliente y el estado actual.
            </Typography>
          </Box>
        </Stack>

        <PostventaFilters
          searchInput={searchInput}
          onSearchInputChange={setSearchInput}
          vendedores={vendedores}
          vendedorId={vendedorId}
          onVendedorChange={setVendedorId}
          tipo={tipo}
          onTipoChange={setTipo}
          estado={estado}
          onEstadoChange={setEstado}
          onClear={clearFilters}
        />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <PostventaTable
          rows={rows}
          loading={loading}
          total={total}
          page={page}
          rowsPerPage={limit}
          subtitle={subtitle}
          updatingId={updatingId}
          onPageChange={(event, nextPage) => setPage(nextPage)}
          onOpenFicha={setSelected}
          onToggleEstado={toggleEstado}
        />
      </Box>

      <PostventaFichaDialog
        open={Boolean(selected)}
        item={selected}
        onClose={() => setSelected(null)}
        onSaved={fetchRows}
      />
    </Box>
  );
}
